import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { nutritionAmount, quantityUnit, resolvePortion, type CreateProduct, type Product, type QuantityUnit } from '@calorie/shared';
import { barcodeFromCamera, barcodeFromImage } from '../lib/barcode.js';
import { haptic, hapticResult } from '../lib/telegram.js';

interface Props {
  onLookup: (barcode: string) => Promise<Product>;
  onCreate: (input: CreateProduct) => Promise<Product>;
  onAdd: (product: Product, amount: number, unit: QuantityUnit) => Promise<void>;
  onClose: () => void;
}

type Step = 'choose' | 'camera' | 'reading' | 'missing' | 'create' | 'found' | 'error';

const emptyDraft = {
  name: '', brand: '', serving: '', basis: 'g' as QuantityUnit,
  kcal: '', protein: '', fat: '', carbs: '',
};

export function BarcodeSheet({ onLookup, onCreate, onAdd, onClose }: Props) {
  const [step, setStep] = useState<Step>('choose');
  const [product, setProduct] = useState<Product | null>(null);
  const [barcode, setBarcode] = useState<string | null>(null);
  const [grams, setGrams] = useState('100');
  const [unitOverride, setUnitOverride] = useState<QuantityUnit | null>(null);
  const unit = unitOverride ?? (product ? quantityUnit(product) : 'g');
  useEffect(() => { setUnitOverride(null); }, [product?.id]);
  const [message, setMessage] = useState('');
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);
  const galleryRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const stopCameraRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (step !== 'camera' || !videoRef.current) return;
    let active = true;
    barcodeFromCamera(videoRef.current, (code) => {
      if (!active) return;
      void lookup(code);
    })
      .then((stop) => {
        if (active) stopCameraRef.current = stop;
        else stop();
      })
      .catch(() => {
        if (!active) return;
        setMessage('Не удалось открыть камеру. Проверьте разрешение камеры для Telegram.');
        setStep('error');
      });
    return () => {
      active = false;
      stopCameraRef.current?.();
      stopCameraRef.current = null;
    };
  }, [step]);

  function close() {
    if (closing || saving) return;
    stopCameraRef.current?.();
    setClosing(true);
    setTimeout(onClose, 200);
  }

  function reset() {
    setStep('choose');
    setProduct(null);
    setBarcode(null);
    setMessage('');
    setDraft(emptyDraft);
    stopCameraRef.current?.();
    stopCameraRef.current = null;
    // Один и тот же файл должно быть можно выбрать повторно.
    if (galleryRef.current) galleryRef.current.value = '';
  }

  async function lookup(code: string) {
    stopCameraRef.current?.();
    stopCameraRef.current = null;
    setBarcode(code);
    setMessage('');
    setStep('reading');
    try {
      const found = await onLookup(code);
      setProduct(found);
      const serving = found.servingSizeG != null
        ? resolvePortion({ raw: found.name, query: found.name, grams: found.servingSizeG, count: null }, null, found) : null;
      setGrams(String(Math.round((quantityUnit(found) === 'ml' ? serving?.volumeMl : serving?.grams) ?? 100)));
      setStep('found');
      hapticResult('success');
    } catch (error) {
      const status = typeof error === 'object' && error && 'status' in error
        ? Number((error as { status: unknown }).status)
        : 0;
      if (status === 404) {
        setMessage('Штрихкод распознан, но такого товара пока нет в Open Food Facts.');
        setStep('missing');
      } else {
        setMessage('Штрихкод распознан, но каталог продуктов сейчас недоступен. Попробуйте ещё раз.');
        setStep('error');
      }
      hapticResult('error');
    }
  }

  async function read(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMessage('Выберите фотографию штрихкода.');
      setStep('error');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setMessage('Фото слишком большое. Максимальный размер — 20 МБ.');
      setStep('error');
      return;
    }

    setMessage('');
    setStep('reading');
    let decoded: string | null = null;
    try {
      const code = await barcodeFromImage(file);
      decoded = code;
      await lookup(code);
    } catch (error) {
      // lookup() уже показал точную ошибку каталога — не затираем её общей
      // ошибкой чтения изображения.
      if (decoded) return;
      const status = typeof error === 'object' && error && 'status' in error
        ? Number((error as { status: unknown }).status)
        : 0;
      setMessage(
        status === 404
          ? 'Штрихкод распознан, но такого товара пока нет в Open Food Facts.'
          : 'Не удалось прочитать штрихкод. Снимите его крупно, ровно и без бликов.',
      );
      setStep('error');
      hapticResult('error');
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!product || saving) return;
    const amount = Number(grams.replace(',', '.'));
    if (!Number.isFinite(amount) || amount < 1 || amount > 5000) {
      setMessage(`Укажите количество от 1 до 5000 ${unit === 'ml' ? 'мл' : 'г'}.`);
      return;
    }
    if (!portion) { setMessage('Неизвестна плотность. Выберите единицу, в которой указаны КБЖУ на упаковке.'); return; }

    setSaving(true);
    setMessage('');
    haptic('medium');
    try {
      await onAdd(product, amount, unit);
      // close() блокирует закрытие во время запроса; после успешного ответа
      // завершаем шторку напрямую, пока React ещё не применил setSaving(false).
      setClosing(true);
      setTimeout(onClose, 200);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Не получилось добавить продукт. Проверьте соединение.');
      hapticResult('error');
      setSaving(false);
    }
  }

  function draftNumber(value: string, max: number): number | null {
    if (!value.trim()) return null;
    const number = Number(value.replace(',', '.'));
    return Number.isFinite(number) && number >= 0 && number <= max ? number : null;
  }

  async function createProduct(event: FormEvent) {
    event.preventDefault();
    if (!barcode || saving) return;
    const kcal = draftNumber(draft.kcal, 1000);
    const protein = draftNumber(draft.protein, 100);
    const fat = draftNumber(draft.fat, 100);
    const carbs = draftNumber(draft.carbs, 100);
    const serving = draft.serving.trim() ? draftNumber(draft.serving, 2000) : null;
    if (draft.name.trim().length < 2) {
      setMessage('Укажите название продукта.');
      return;
    }
    if ([kcal, protein, fat, carbs].some((value) => value === null)) {
      setMessage('Проверьте КБЖУ: калории 0–1000, Б/Ж/У 0–100.');
      return;
    }
    if (draft.basis === 'g' && draft.serving.trim() && (serving === null || serving < 1)) {
      setMessage('Вес порции должен быть от 1 до 2000 г.');
      return;
    }

    setSaving(true);
    setMessage('');
    haptic('medium');
    try {
      const created = await onCreate({
        name: draft.name.trim(),
        brand: draft.brand.trim() || null,
        barcode,
        // Поле API хранит именно массу. Для напитка без известной плотности
        // объём порции пользователь укажет уже при добавлении в дневник.
        servingSizeG: draft.basis === 'g' ? serving : null,
        nutritionBasis: draft.basis,
        per100g: { kcal: kcal!, protein: protein!, fat: fat!, carbs: carbs!, fiber: null, sugars: null, salt: null },
      });
      setProduct(created);
      setGrams(String(Math.round(created.servingSizeG ?? 100)));
      setStep('found');
      hapticResult('success');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Не получилось сохранить продукт.');
      hapticResult('error');
    } finally {
      setSaving(false);
    }
  }

  const amount = Number(grams.replace(',', '.'));
  const portion = product && Number.isFinite(amount) && amount > 0 && amount <= 5000
    ? resolvePortion({ raw: product.name, query: product.name, grams: unit === 'g' ? amount : null, volumeMl: unit === 'ml' ? amount : null, count: null }, null, product) : null;
  const ratio = portion ? nutritionAmount(portion) / 100 : 0;

  return (
    <div
      className={`sheet-backdrop${closing ? ' closing' : ''}`}
      onClick={close}
      role="presentation"
    >
      <div
        className={`sheet barcode-sheet glass${closing ? ' closing' : ''}`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Сканирование штрихкода"
      >
        <div className="sheet-grabber" />
        <div className="sheet-head">
          <div className="sheet-titles">
            <span className="sheet-title">Штрихкод</span>
            <span className="sheet-source">Фото распознаётся только на вашем устройстве</span>
          </div>
          <button className="icon-btn" onClick={close} aria-label="Закрыть" disabled={saving}>
            ✕
          </button>
        </div>

        <input
          ref={galleryRef}
          className="barcode-file"
          type="file"
          accept="image/*"
          onChange={read}
          tabIndex={-1}
          aria-hidden="true"
        />

        <div className="sheet-body barcode-body">
          {step === 'choose' && (
            <div className="barcode-choose">
              <div className="barcode-illustration" aria-hidden="true">
                <i /><i /><i /><i /><i /><i /><i /><i />
              </div>
              <p>Поместите весь штрихкод в кадр. Лучше снимать прямо и при хорошем свете.</p>
              <button className="btn barcode-primary" onClick={() => setStep('camera')}>
                <CameraIcon />
                Сканировать камерой
              </button>
              <button className="btn btn-ghost barcode-secondary" onClick={() => galleryRef.current?.click()}>
                <PhotoIcon />
                Выбрать из галереи
              </button>
            </div>
          )}

          {step === 'camera' && (
            <div className="barcode-camera">
              <div className="barcode-video-frame">
                <video ref={videoRef} muted playsInline aria-label="Изображение с камеры" />
                <i className="barcode-camera-line" />
              </div>
              <p>Наведите камеру на штрихкод — снимок делать не нужно.</p>
              <button className="barcode-rescan" type="button" onClick={reset}>
                Отмена
              </button>
            </div>
          )}

          {step === 'reading' && (
            <div className="barcode-reading" aria-live="polite">
              <div className="barcode-scan-frame">
                <div className="barcode-scan-line" />
              </div>
              <span>Читаю штрихкод…</span>
            </div>
          )}

          {step === 'error' && (
            <div className="barcode-result-state" role="alert">
              <span className="barcode-error-icon">!</span>
              <strong>Не получилось</strong>
              <p>{message}</p>
              {barcode && <small>Код: {barcode}</small>}
              <button className="btn" onClick={reset}>Попробовать другое фото</button>
            </div>
          )}

          {step === 'missing' && (
            <div className="barcode-result-state" role="alert">
              <span className="barcode-error-icon">?</span>
              <strong>Товара ещё нет</strong>
              <p>{message}</p>
              {barcode && <small>Код: {barcode}</small>}
              <button className="btn" onClick={() => { setMessage(''); setStep('create'); }}>
                Добавить продукт вручную
              </button>
              <button className="barcode-rescan" type="button" onClick={reset}>Другой штрихкод</button>
            </div>
          )}

          {step === 'create' && barcode && (
            <form className="barcode-create" onSubmit={createProduct}>
              <div className="barcode-create-code">Штрихкод <strong>{barcode}</strong></div>
              <label>
                <span>Название</span>
                <input autoFocus value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Например, йогурт греческий" />
              </label>
              <label>
                <span>Бренд <i>необязательно</i></span>
                <input value={draft.brand} onChange={(e) => setDraft({ ...draft, brand: e.target.value })} placeholder="Название производителя" />
              </label>
              <label>
                <span>Пищевая ценность указана</span>
                <select value={draft.basis} onChange={(e) => setDraft({ ...draft, basis: e.target.value as QuantityUnit })}>
                  <option value="g">на 100 г</option>
                  <option value="ml">на 100 мл</option>
                </select>
              </label>
              <div className="barcode-create-macros">
                {([
                  ['kcal', 'Ккал', '0–1000'],
                  ['protein', 'Белки', '0–100'],
                  ['fat', 'Жиры', '0–100'],
                  ['carbs', 'Углеводы', '0–100'],
                ] as const).map(([key, label, placeholder]) => (
                  <label key={key}>
                    <span>{label}</span>
                    <input inputMode="decimal" value={draft[key]} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} placeholder={placeholder} />
                  </label>
                ))}
              </div>
              {draft.basis === 'g' && (
                <label>
                  <span>Обычная порция, г <i>необязательно</i></span>
                  <input inputMode="decimal" value={draft.serving} onChange={(e) => setDraft({ ...draft, serving: e.target.value })} placeholder="Например, 150" />
                </label>
              )}
              {message && <p className="barcode-message">{message}</p>}
              <button className="btn" type="submit" disabled={saving}>
                {saving ? <span className="composer-spinner" /> : 'Сохранить продукт'}
              </button>
              <button className="barcode-rescan" type="button" onClick={() => setStep('missing')} disabled={saving}>Назад</button>
            </form>
          )}

          {step === 'found' && product && (
            <form className="barcode-product" onSubmit={submit}>
              <div className="barcode-product-head">
                {product.imageUrl ? (
                  <img src={product.imageUrl} alt="" />
                ) : (
                  <div className="barcode-product-placeholder"><BarcodeIcon /></div>
                )}
                <div>
                  <strong>{product.name}</strong>
                  {product.brand && <span>{product.brand}</span>}
                  <small>{barcode}</small>
                </div>
              </div>

              <div className="barcode-per100">
                <span><b>{Math.round(product.per100g.kcal)}</b> ккал</span>
                <span>Б <b>{product.per100g.protein}</b></span>
                <span>Ж <b>{product.per100g.fat}</b></span>
                <span>У <b>{product.per100g.carbs}</b></span>
                <small>на 100 {product.nutritionBasis === 'ml' ? 'мл' : 'г'}</small>
              </div>

              <label className="barcode-grams">
                <span>Сколько вы употребили?</span>
                <div>
                  <input
                    value={grams}
                    onChange={(event) => setGrams(event.target.value)}
                    inputMode="decimal"
                    enterKeyHint="done"
                    aria-label={unit === 'ml' ? 'Объём порции в миллилитрах' : 'Вес порции в граммах'}
                    disabled={saving}
                  />
                  <select aria-label="Единица порции" value={unit} onChange={(e) => setUnitOverride(e.target.value as QuantityUnit)} disabled={saving}>
                    <option value="ml">мл</option>
                    <option value="g">г</option>
                  </select>
                </div>
              </label>

              <div className="barcode-total">
                {portion ? <>Получится <strong>{Math.round(product.per100g.kcal * ratio)} ккал</strong></> : 'Для перевода неизвестна плотность. Выберите единицу с этикетки.'}
              </div>
              {message && <p className="barcode-message">{message}</p>}
              <button className="btn" type="submit" disabled={saving || ratio <= 0}>
                {saving ? <span className="composer-spinner" /> : 'Добавить в дневник'}
              </button>
              <button className="barcode-rescan" type="button" onClick={reset} disabled={saving}>
                Сканировать другой товар
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function CameraIcon() {
  return <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M8.2 6.5 9.4 4.8h5.2l1.2 1.7h2.7A2.5 2.5 0 0 1 21 9v8.5a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 17.5V9a2.5 2.5 0 0 1 2.5-2.5h2.7Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><circle cx="12" cy="13" r="3.2" stroke="currentColor" strokeWidth="1.8"/></svg>;
}

function PhotoIcon() {
  return <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="3" stroke="currentColor" strokeWidth="1.8"/><circle cx="9" cy="10" r="2" stroke="currentColor" strokeWidth="1.8"/><path d="m5.5 18 4.4-4.5 3.1 3 2.1-2 3.4 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}

function BarcodeIcon() {
  return <svg viewBox="0 0 36 28" fill="none" aria-hidden="true"><path d="M3 3v22M7 3v22M12 3v22M15 3v22M21 3v22M25 3v22M28 3v22M33 3v22" stroke="currentColor" strokeWidth="2"/></svg>;
}
